import React, { useCallback, useEffect, useState } from 'react';
import { Alert, ExportScopeModal } from '@leapeasy/ui-kit';

/**
 * CSV export scope modal for ui-kit {@link Table} lists. Expects the same ref passed to {@link Table}.
 * Full-dataset totals are loaded on demand via {@link onLoadFullDatasetTotal} when the modal opens.
 */
export function ListExportScopeModal({
  open,
  onClose,
  tableRef,
  title,
  currentPageRowCount,
  paginationTotalItems,
  fullDatasetTotal,
  onLoadFullDatasetTotal,
  isLoadingFullDatasetTotal = false,
  selectedExportCount = 0,
  enableFiltered,
  enableFull,
  enableSelected,
}) {
  const [pending, setPending] = useState(false);
  const [errorText, setErrorText] = useState('');
  const [resolvedFullTotal, setResolvedFullTotal] = useState(fullDatasetTotal);

  useEffect(() => {
    setResolvedFullTotal(fullDatasetTotal);
  }, [fullDatasetTotal]);

  useEffect(() => {
    if (!open || !enableFull || typeof onLoadFullDatasetTotal !== 'function') {
      return undefined;
    }

    if (fullDatasetTotal != null) {
      return undefined;
    }

    let cancelled = false;
    void onLoadFullDatasetTotal().then((total) => {
      if (!cancelled && total != null && Number.isFinite(total)) {
        setResolvedFullTotal(total);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [open, enableFull, onLoadFullDatasetTotal, fullDatasetTotal]);

  const onConfirmExport = useCallback(
    async (scope) => {
      const exportCsv = tableRef?.current?.exportCsv;
      if (typeof exportCsv !== 'function') {
        return;
      }
      setErrorText('');
      setPending(true);
      try {
        await exportCsv(scope);
        onClose();
      } catch (error) {
        const nextError =
          error?.message || 'Export failed. Please try again or choose a smaller scope.';
        setErrorText(nextError);
      } finally {
        setPending(false);
      }
    },
    [onClose, tableRef],
  );

  const filteredTotal =
    typeof paginationTotalItems === 'number' && Number.isFinite(paginationTotalItems)
      ? paginationTotalItems
      : undefined;

  return (
    <>
      <ExportScopeModal
        open={open}
        onClose={onClose}
        title={title}
        currentPageRowCount={currentPageRowCount}
        filteredTotal={enableFiltered ? filteredTotal : undefined}
        fullDatasetTotal={enableFull ? resolvedFullTotal : undefined}
        selectedCount={enableSelected ? selectedExportCount : 0}
        showFiltered={Boolean(enableFiltered)}
        showFull={Boolean(enableFull)}
        showSelected={Boolean(enableSelected)}
        onConfirmExport={onConfirmExport}
        isExporting={pending || isLoadingFullDatasetTotal}
      />
      {open && errorText ? (
        <div style={{ marginTop: '12px' }}>
          <Alert
            type="error"
            variant="contained"
            showBorder
            description={errorText}
            showClose={false}
          />
        </div>
      ) : null}
    </>
  );
}
